// admin/src/components/NotificationBell.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './NotificationBell.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://tripsie.in/api';

const formatTimeAgo = (date) => {
  const diff    = Date.now() - new Date(date);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1)  return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)   return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7)     return `${days}d ago`;
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const getTypeIcon = (type) => {
  switch (type) {
    case 'new_booking':  return '🎉';
    case 'cancellation': return '❌';
    case 'payment':      return '💰';
    default:             return '🔔';
  }
};

const NotificationBell = () => {
  const [notifications, setNotifications]   = useState([]);
  const [unreadCount,   setUnreadCount]     = useState(0);
  const [isOpen,        setIsOpen]          = useState(false);
  const [loading,       setLoading]         = useState(false);
  const [hasNewPulse,   setHasNewPulse]     = useState(false);

  const panelRef   = useRef(null);
  const sseRef     = useRef(null);
  const isMounted  = useRef(true);

  /* ── Fetch notifications from REST ──────────────────────────────────── */
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const res   = await fetch(`${API_BASE_URL}/notifications?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && isMounted.current) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Fetch notifications error:', err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  /* ── SSE connection ──────────────────────────────────────────────────── */
  const connectSSE = useCallback(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    // Close any existing connection
    if (sseRef.current) {
      sseRef.current.close();
    }

    const url = `${API_BASE_URL}/notifications/stream?token=${encodeURIComponent(token)}`;
    const es  = new EventSource(url);
    sseRef.current = es;

    es.addEventListener('connected', () => {
      console.log('🔔 Notification SSE connected');
    });

    es.addEventListener('notification', (e) => {
      try {
        const notification = JSON.parse(e.data);
        if (!isMounted.current) return;

        // Prepend new notification to the list
        setNotifications((prev) => [notification, ...prev].slice(0, 50));
        setUnreadCount((prev) => prev + 1);

        // Pulse animation for new notification
        setHasNewPulse(true);
        setTimeout(() => {
          if (isMounted.current) setHasNewPulse(false);
        }, 3000);

        // Browser notification (if permitted)
        if (Notification.permission === 'granted') {
          new Notification(notification.title, {
            body: notification.message,
            icon: '/favicon.ico',
          });
        }
      } catch (err) {
        console.error('SSE parse error:', err);
      }
    });

    es.onerror = () => {
      // Auto-reconnect after 5s
      es.close();
      setTimeout(() => {
        if (isMounted.current) connectSSE();
      }, 5000);
    };
  }, []);

  /* ── Mount / unmount ─────────────────────────────────────────────────── */
  useEffect(() => {
    isMounted.current = true;
    fetchNotifications();
    connectSSE();

    // Request browser notification permission
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      isMounted.current = false;
      if (sseRef.current) sseRef.current.close();
    };
  }, [fetchNotifications, connectSSE]);

  /* ── Close panel on outside click ───────────────────────────────────── */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /* ── Actions ─────────────────────────────────────────────────────────── */
  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Mark read error:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_BASE_URL}/notifications/mark-all-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Mark all read error:', err);
    }
  };

  const deleteNotification = async (id, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_BASE_URL}/notifications/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const deleted = notifications.find((n) => n._id === id);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      if (deleted && !deleted.isRead) setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleBellClick = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) fetchNotifications(); // refresh when opening
  };

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div className="nb" ref={panelRef}>
      {/* Bell Button */}
      <button
        className={`nb__bell ${hasNewPulse ? 'nb__bell--pulse' : ''}`}
        onClick={handleBellClick}
        aria-label="Notifications"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className={`nb__badge ${hasNewPulse ? 'nb__badge--new' : ''}`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="nb__panel">
          {/* Header */}
          <div className="nb__header">
            <div className="nb__header-left">
              <h3 className="nb__title">Notifications</h3>
              {unreadCount > 0 && (
                <span className="nb__count">{unreadCount} new</span>
              )}
            </div>
            {unreadCount > 0 && (
              <button className="nb__mark-all" onClick={markAllAsRead}>
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="nb__list">
            {loading && notifications.length === 0 ? (
              <div className="nb__loading">
                <div className="nb__spinner" />
                <p>Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="nb__empty">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.5">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n._id}
                  className={`nb__item ${!n.isRead ? 'nb__item--unread' : ''}`}
                  onClick={() => !n.isRead && markAsRead(n._id)}
                >
                  <div className="nb__item-icon">{getTypeIcon(n.type)}</div>
                  <div className="nb__item-body">
                    <p className="nb__item-title">{n.title}</p>
                    <p className="nb__item-msg">{n.message}</p>
                    {n.data?.amount > 0 && (
                      <p className="nb__item-amount">
                        ₹{n.data.amount.toLocaleString('en-IN')}
                      </p>
                    )}
                    <p className="nb__item-time">{formatTimeAgo(n.createdAt)}</p>
                  </div>
                  {!n.isRead && <div className="nb__item-dot" />}
                  <button
                    className="nb__item-delete"
                    onClick={(e) => deleteNotification(n._id, e)}
                    aria-label="Delete notification"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Live indicator */}
          <div className="nb__footer">
            <span className="nb__live-dot" />
            <span className="nb__live-text">Live updates enabled</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;